import axios from 'axios'
import store from '@/store'
import router from '@/router'
import message from '@/utils/message'
import eventBus from '@/utils/eventBus'
import { enterLoginExpiredFlowOnce } from '@/utils/loginExpiredGate'

// 基础配置：请根据环境自行调整
// 开发环境使用代理路径 /api，生产环境使用实际 API 地址
const baseURL = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_BASE_API
  : '/api'

const service = axios.create({
  baseURL: baseURL,
  timeout: 15000
})

// 用于存储所有正在进行的请求
const pendingRequests = new Map()

// 创建取消令牌的函数
const getCancelToken = () => {
  return axios.CancelToken.source()
}

// 请求拦截：统一携带 Token
service.interceptors.request.use(
  config => {
    const token = store?.getters?.['user/token'] || localStorage.getItem('token')
    if (token) {
      // 兼容后台要求的 TOKEN 字段，同时保留原有 user-token
      config.headers.TOKEN = token
      config.headers['user-token'] = token
    }

    // 如果以 /chenduo-app 开头，则直接使用完整域名 + 请求地址，绕过 baseURL 与代理
    if (config.url && config.url.indexOf('/chenduo-app') === 0) {
      const fullApi = process.env.VUE_APP_BASE_API || ''
      // 从 VUE_APP_BASE_API 中提取域名部分，例如：https://app.chenduodianjing.com
      let domain = fullApi
      const splitIndex = fullApi.indexOf('/chenduo-app')
      if (splitIndex !== -1) {
        domain = fullApi.substring(0, splitIndex)
      }
      // 使用绝对地址，避免再拼接 baseURL
      config.baseURL = ''
      config.url = `${domain}${config.url}`
    }

    // 所有 POST 请求默认使用 application/x-www-form-urlencoded
    const method = (config.method || '').toLowerCase()
    if (
      method === 'post' ||
      method === 'put' ||
      method === 'patch'
    ) {
      const hasCustomContentType = config.headers && config.headers['Content-Type']
      // 如果调用方显式设置了 Content-Type（例如 application/json），则不再强制改为 x-www-form-urlencoded，也不再手动序列化
      if (!hasCustomContentType) {
        // 如果不是 FormData，则序列化为 x-www-form-urlencoded
        const isFormData = config.data instanceof FormData
        if (!isFormData && config.data && typeof config.data === 'object') {
          const params = new URLSearchParams()
          Object.keys(config.data).forEach(key => {
            const value = config.data[key]
            if (value !== undefined && value !== null) {
              params.append(key, value)
            }
          })
          config.data = params.toString()
        }

        // 统一设置 Content-Type（FormData 由浏览器自动设置）
        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8'
        }
      }
    }

    // 为每个请求添加取消令牌
    const cancelToken = getCancelToken()
    config.cancelToken = cancelToken.token

    const paramsStr = config.params ? JSON.stringify(config.params) : ''
    const dataStr = config.data ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data)) : ''
    const baseKey = `${config.method}_${config.url}_${paramsStr}_${dataStr}`
    const methodLower = (config.method || 'get').toLowerCase()
    // 仅对写操作做「相同参数取消上一次」；GET 等读操作并发时若共用一个 key 会误取消，改为每次唯一 mapKey
    const dedupeMutating = ['post', 'put', 'patch', 'delete'].includes(methodLower)
    let mapKey = baseKey
    if (!dedupeMutating) {
      mapKey = `${baseKey}_${Date.now()}_${Math.random().toString(36).slice(2)}`
    }
    config.__pendingMapKey = mapKey

    if (dedupeMutating && pendingRequests.has(baseKey)) {
      pendingRequests.get(baseKey).cancel()
    }
    pendingRequests.set(mapKey, cancelToken)

    return config
  },
  error => Promise.reject(error)
)

// 清除所有正在进行的请求
const cancelAllRequests = (cancelMessage) => {
  pendingRequests.forEach((cancelToken, key) => {
    cancelToken.cancel(cancelMessage || '请求已取消')
    pendingRequests.delete(key)
  })
}

// 清除用户缓存和登录信息
const clearUserCache = () => {
  // 清除localStorage中的所有数据（仅清理与登录相关的关键字段，避免误删其他本地数据）
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('rememberedPhone')
  // 清除store中的用户信息
  if (store && store.dispatch) {
    store.dispatch('user/logout')
  }
}

// 处理登录失效的逻辑（并发多请求只提示一次）
const handleLoginExpired = (msg) => {
  cancelAllRequests('登录失效')
  if (!enterLoginExpiredFlowOnce()) {
    return
  }
  clearUserCache()
  if (msg) {
    message.error(msg)
  }
  if (eventBus && eventBus.$emit) {
    eventBus.$emit('show-login-dialog')
  }
}

// 响应拦截：统一处理业务错误
service.interceptors.response.use(
  response => {
    const res = response.data || {}

    const paramsStr = response.config.params ? JSON.stringify(response.config.params) : ''
    const dataStr = response.config.data ? (typeof response.config.data === 'string' ? response.config.data : JSON.stringify(response.config.data)) : ''
    const requestKey = `${response.config.method}_${response.config.url}_${paramsStr}_${dataStr}`
    pendingRequests.delete(response.config.__pendingMapKey || requestKey)

    // 约定：后端以 code === 1 为成功
    if (res.code == 1) {
      return res
    }

    // 处理特定的错误码
    const code = res.code

    // 业务约定：code === 2 代表登录状态失效/未登录
    if (code == 2) {
      handleLoginExpired(res.msg || res.message || '登录已失效，请重新登录')
      return Promise.reject(new Error('登录已失效，请重新登录'))
    }

    // 当返回code为-201, -202, -203时，跳转到登录页面并提示登录失效
    if (code == '-201' || code == '-202' || code == '-203') {
      handleLoginExpired('登录失效，请重新登录')
      return Promise.reject(new Error('登录失效，请重新登录'))
    }

    // 当返回code为401时，跳转到登录页面并提示未授权
    if (code == '401') {
      handleLoginExpired('未授权，请重新登录')
      return Promise.reject(new Error('未授权，请重新登录'))
    }

    // 其他错误码正常处理
    const errorMessage = res.msg || res.message || '请求失败'
    return Promise.reject(new Error(errorMessage))

  },
  error => {
    // 移除已完成的请求（包括被取消的，使用与请求拦截器相同的key生成方式）
    if (error.config) {
      const paramsStr = error.config.params ? JSON.stringify(error.config.params) : ''
      const dataStr = error.config.data ? (typeof error.config.data === 'string' ? error.config.data : JSON.stringify(error.config.data)) : ''
      const requestKey = `${error.config.method}_${error.config.url}_${paramsStr}_${dataStr}`
      pendingRequests.delete(error.config.__pendingMapKey || requestKey)
    }

    // 处理HTTP状态码401（未授权）
    if (error.response && error.response.status === 401) {
      handleLoginExpired('未授权，请重新登录')
      return Promise.reject(new Error('未授权，请重新登录'))
    }

    // 如果是取消请求的错误，不显示错误信息
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // 其他错误正常处理
    return Promise.reject(error)
  }
)

/** 业务里 catch 时判断是否为请求去重等导致的取消，避免弹「canceled」 */
export function isRequestCanceled(error) {
  if (!error) return false
  if (axios.isCancel(error)) return true
  const m = (error && error.message) || ''
  return typeof m === 'string' && /cancel/i.test(m)
}

export default service

